// 🔹 React Core
import React, { useEffect, useRef, useState } from 'react';

// 🔹 React Ecosystem
import { useLocation } from 'react-router-dom';

// 🔹 UI Component Libraries
import { Tooltip } from 'antd';
import type { TooltipProps } from 'antd';

// 🔹 Constants
const AUTO_CLOSE_DELAY = 1500;

type AutoCloseTooltipProps = TooltipProps & {
  children: React.ReactNode;
  title?: React.ReactNode;
  forceHidden?: boolean;
  delay?: number;
};

export default function AutoCloseTooltip({ children, title, trigger = 'hover', forceHidden, delay = AUTO_CLOSE_DELAY, ...rest }: AutoCloseTooltipProps) {
  const [open, setOpen] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const location = useLocation();

  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 992 : false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleOpenChange = (visible: boolean) => {
    setOpen(visible);

    const isClickTrigger = Array.isArray(trigger) ? trigger.includes('click') : trigger === 'click';
    if (visible && (isMobile || isClickTrigger)) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setOpen(false);
      }, delay);
    }
  };

  useEffect(() => {
    const segments = location?.pathname?.split('/');
    if (segments?.includes('projects')) {
      document.documentElement.classList.toggle('antd-tooltip-open', open);
    } else {
      document.documentElement.classList.remove('antd-tooltip-open');
    }
    return () => {
      document.documentElement.classList.remove('antd-tooltip-open');
    };
  }, [open, location.pathname]);

  return (
    <Tooltip
      {...rest}
      title={title}
      open={forceHidden ? false : open}
      trigger={isMobile ? ['click'] : trigger}
      onOpenChange={handleOpenChange}
    >
      {children}
    </Tooltip>
  );
}
